import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, CircleAlert, Download, FileSpreadsheet, Upload } from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';
import { toast } from 'sonner';
import { api, descargar, mensajeError } from '@/api/cliente';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Insignia, Pestana, PestanaContenido, Pestanas, PestanasLista, Tabla, Tarjeta, Td, Th, Tr, Vacio } from '@/components/ui/data';
import { Dialogo } from '@/components/ui/overlay';
import { cn } from '@/lib/utils';
import { avisarError } from '@/lib/avisos';

const MAX_BYTES = 2 * 1024 * 1024;
const COLUMNAS = {
  Citas: ['fecha', 'hora', 'cliente', 'telefono', 'origen', 'servicio', 'barbero'],
  Ventas: ['fecha', 'hora', 'cliente', 'telefono', 'concepto', 'cantidad', 'precio', 'metodo', 'moneda', 'barbero', 'cobro'],
} as const;
type Hoja = keyof typeof COLUMNAS;

interface Fila {
  hoja: Hoja;
  fila: number;
  datos: Record<string, string | number | null>;
  errores: string[];
}
interface Vista {
  importacionId: string;
  validas: number;
  conErrores: number;
  filas: Fila[];
}
interface Resultado {
  aplicadas: number;
  fallidas: { hoja: Hoja; fila: number; error: string }[];
}

export function ImportacionPage() {
  const entrada = useRef<HTMLInputElement>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [vista, setVista] = useState<Vista | null>(null);
  const [confirmar, setConfirmar] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);

  const subir = useMutation({
    mutationFn: (archivo: File) => {
      const datos = new FormData();
      datos.append('archivo', archivo);
      return api<Vista>('/importacion/previsualizar', { method: 'POST', body: datos });
    },
    onSuccess: (v) => {
      setVista(v);
      setResultado(null);
    },
    onError: (e) => avisarError(e),
  });
  const aplicar = useMutation({
    mutationFn: () => api<Resultado>(`/importacion/${vista!.importacionId}/aplicar`, { method: 'POST' }),
    onSuccess: (r) => {
      setResultado(r);
      setConfirmar(false);
      toast.success(`${r.aplicadas} registros importados`);
    },
    onError: (e) => avisarError(e),
  });

  const elegir = (archivo: File | undefined) => {
    if (!archivo) return;
    if (!/\.xlsx$/i.test(archivo.name)) return toast.error('Solo se aceptan archivos de Excel (.xlsx)');
    if (archivo.size > MAX_BYTES) return toast.error('El archivo supera los 2 MB');
    subir.mutate(archivo);
  };
  const soltar = (e: DragEvent) => {
    e.preventDefault();
    setArrastrando(false);
    elegir(e.dataTransfer.files[0]);
  };

  const hojas = (Object.keys(COLUMNAS) as Hoja[]).filter((h) => vista?.filas.some((f) => f.hoja === h));
  const fallo = (f: Fila) => resultado?.fallidas.find((x) => x.hoja === f.hoja && x.fila === f.fila);

  return (
    <ModuleLayout titulo="Caja" secciones={SECCIONES_POR_MODULO.caja}>
      <EncabezadoPagina
        titulo="Importar apagón"
        descripcion="Sube en Excel las citas y ventas anotadas en papel mientras no hubo sistema. Revisa la vista previa antes de aplicar."
        acciones={
          <Button variante="secundario" onClick={() => void descargar('/importacion/plantilla.xlsx', undefined, 'plantilla-apagon.xlsx').catch((e) => avisarError(e))}>
            <Download /> Descargar plantilla
          </Button>
        }
      />

      <button
        type="button"
        onClick={() => entrada.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setArrastrando(true);
        }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={soltar}
        className={cn(
          'mb-5 flex w-full cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border bg-surface px-6 py-8 text-center transition-colors hover:border-accent focus-visible:border-accent focus-visible:outline-none',
          arrastrando && 'border-accent bg-accent-soft',
        )}
      >
        <FileSpreadsheet className="size-8 text-accent" aria-hidden />
        <span className="text-[15px] font-medium">{subir.isPending ? 'Leyendo archivo…' : 'Arrastra aquí el archivo o haz clic para elegirlo'}</span>
        <span className="text-[13px] text-foreground-muted">Solo .xlsx de hasta 2 MB · máximo 1000 filas por hoja</span>
      </button>
      <input ref={entrada} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="sr-only" onChange={(e) => { elegir(e.target.files?.[0]); e.target.value = ''; }} />

      {!vista ? (
        <Tarjeta>
          <Vacio icono={<Upload />} titulo="Aún no hay archivo" descripcion="Descarga la plantilla, captura lo del apagón y súbela aquí." />
        </Tarjeta>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Insignia tono="exito">
              <CheckCircle2 /> {vista.validas} listas para importar
            </Insignia>
            {vista.conErrores > 0 && (
              <Insignia tono="peligro">
                <CircleAlert /> {vista.conErrores} con errores (no se importarán)
              </Insignia>
            )}
            <div className="ml-auto">
              {resultado ? (
                <Insignia tono="solido">Importación aplicada: {resultado.aplicadas} registros</Insignia>
              ) : (
                <Button onClick={() => setConfirmar(true)} disabled={vista.validas === 0}>
                  Importar {vista.validas} {vista.validas === 1 ? 'fila' : 'filas'}
                </Button>
              )}
            </div>
          </div>

          {hojas.length === 0 ? (
            <Tarjeta>
              <Vacio icono={<FileSpreadsheet />} titulo="El archivo no tiene filas" descripcion="Verifica que las hojas se llamen «Citas» y «Ventas»." />
            </Tarjeta>
          ) : (
            <Pestanas defaultValue={hojas[0]}>
              <PestanasLista>
                {hojas.map((h) => (
                  <Pestana key={h} value={h}>
                    {h} ({vista.filas.filter((f) => f.hoja === h).length})
                  </Pestana>
                ))}
              </PestanasLista>
              {hojas.map((h) => (
                <PestanaContenido key={h} value={h}>
                  <Tarjeta className="mt-3 overflow-x-auto">
                    <Tabla>
                      <thead>
                        <tr>
                          <Th>Fila</Th>
                          <Th>Estado</Th>
                          {COLUMNAS[h].map((c) => (
                            <Th key={c}>
                              {c === 'telefono' ? 'teléfono' : c === 'metodo' ? 'método' : c === 'cobro' ? 'cobró' : c}
                            </Th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {vista.filas
                          .filter((f) => f.hoja === h)
                          .map((f) => {
                            const error = fallo(f);
                            const aplicada = resultado && !f.errores.length && !error;
                            return (
                              <Tr key={f.fila} className={cn(f.errores.length > 0 && 'bg-destructive-soft/40')}>
                                <Td className="cifras text-foreground-muted">{f.fila}</Td>
                                <Td className="min-w-56">
                                  {f.errores.length ? (
                                    <ul className="text-[12.5px] text-destructive">
                                      {f.errores.map((e) => (
                                        <li key={e}>{e}</li>
                                      ))}
                                    </ul>
                                  ) : error ? (
                                    <span className="text-[12.5px] text-destructive">{error.error}</span>
                                  ) : aplicada ? (
                                    <Insignia tono="exito">Importada</Insignia>
                                  ) : (
                                    <Insignia tono="info">Lista</Insignia>
                                  )}
                                </Td>
                                {COLUMNAS[h].map((c) => (
                                  <Td key={c} className="whitespace-nowrap">
                                    {f.datos[c] ?? <span className="text-foreground-muted">—</span>}
                                  </Td>
                                ))}
                              </Tr>
                            );
                          })}
                      </tbody>
                    </Tabla>
                  </Tarjeta>
                </PestanaContenido>
              ))}
            </Pestanas>
          )}
        </>
      )}

      {confirmar && vista && (
        <Dialogo
          abierto
          ancho="sm"
          onCambiarAbierto={setConfirmar}
          titulo="¿Importar los registros?"
          descripcion="Las citas y ventas se registrarán con origen «importación». Las ventas quedan pagadas fuera de turno y no alteran el arqueo de caja. Esta acción no se puede repetir con el mismo archivo."
          pie={
            <>
              <Button variante="secundario" onClick={() => setConfirmar(false)}>
                Cancelar
              </Button>
              <Button onClick={() => aplicar.mutate()} cargando={aplicar.isPending}>
                Importar {vista.validas}
              </Button>
            </>
          }
        >
          {vista.conErrores > 0 && <p className="text-[14px] text-foreground-muted">Las {vista.conErrores} filas con errores se omitirán; corrígelas y súbelas en otro archivo.</p>}
        </Dialogo>
      )}
    </ModuleLayout>
  );
}
